import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload, User, Link, Loader2, Camera, Trash2, Linkedin, Twitter, Facebook, Instagram, Globe, Briefcase, MapPin, FileText, Building2, Palette } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import PronounsPicker from '../../components/PronounsPicker';
import TimezoneSelect from '../../components/TimezoneSelect';
import { browserTimeZone } from '../../lib/timezone';
import { presignedUpload, uploadErrorText } from '../../lib/presignedUpload';
import SettingsLayout from './SettingsLayout';
import ReferralIdCard from '../../components/settings/ReferralIdCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SettingsProfilePage() {
    const { user, getAuthHeader } = useAuth();
    const { pref: themePref, setTheme } = useTheme();
    const [profile, setProfile] = useState({
        name: user?.name || '', picture: user?.picture || '', picture_s3_key: '',
        title: '', phone: '', timezone: '', bio: '', pronouns: '',
        linkedin_url: '', twitter_url: '', facebook_url: '', instagram_url: '', personal_website: '',
        is_independent_consultant: false,
        company_name: '', company_street: '', company_city: '', company_state: '',
        company_zip: '', company_country: '', company_website: '', company_logo_url: '', company_logo_s3_key: '', company_abn: '',
    });
    const [profileLoaded, setProfileLoaded] = useState(false);
    const [saving, setSaving] = useState(false);
    const [uploadingPhoto, setUploadingPhoto] = useState(false);
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const profilePhotoRef = useRef(null);
    const companyLogoRef = useRef(null);

    // Load profile on mount
    useEffect(() => {
        axios.get(`${API}/users/profile`, { headers: getAuthHeader() })
            .then(res => {
                if (res.data) {
                    setProfile(prev => ({ ...prev, ...res.data }));
                    setProfileLoaded(true);
                }
            })
            .catch(() => setProfileLoaded(true));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleImageUpload = async (file, field, s3KeyField, setUploading) => {
        if (!file) return;
        if (!file.type.startsWith('image/')) {
            toast.error('Please select an image file');
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            toast.error('Image must be under 5MB');
            return;
        }
        setUploading(true);
        try {
            // Get a presigned POST — the policy pins type + size (CASA 5.2.1)
            const urlRes = await axios.post(`${API}/users/profile/upload-url`, {
                file_name: file.name,
                mime_type: file.type,
                size: file.size,
            }, { headers: getAuthHeader() });
            const { upload, s3_key } = urlRes.data;

            // Upload directly to S3; the object store refuses a wrong type/size
            await presignedUpload(upload, file);

            // Save s3_key to profile — backend will generate download URL on next fetch
            await axios.put(`${API}/users/profile`, {
                [s3KeyField]: s3_key,
            }, { headers: getAuthHeader() });

            // Show local preview immediately
            const localUrl = URL.createObjectURL(file);
            setProfile(p => ({ ...p, [field]: localUrl, [s3KeyField]: s3_key }));
            toast.success('Photo uploaded');
        } catch (err) {
            console.error('Upload failed:', err);
            toast.error(uploadErrorText(err, 'Failed to upload photo'));
        } finally {
            setUploading(false);
        }
    };

    const removeImage = async (field, s3KeyField) => {
        try {
            await axios.put(`${API}/users/profile`, {
                [field]: '', [s3KeyField]: '',
            }, { headers: getAuthHeader() });
            setProfile(p => ({ ...p, [field]: '', [s3KeyField]: '' }));
            toast.success('Photo removed');
        } catch {
            toast.error('Failed to remove photo');
        }
    };

    const saveProfile = async () => {
        setSaving(true);
        try {
            // Strip `picture` / `company_logo_url` from the PUT — those are
            // managed server-side via `picture_s3_key` / `company_logo_s3_key`.
            // The local-preview path uses URL.createObjectURL which produces a
            // `blob:` URL that's only valid in the current tab; sending it
            // would overwrite the real presigned URL in the DB and break the
            // Header avatar + email-signature thumbnail for every other
            // surface. See v2.16.34 fix.
            const { picture, company_logo_url, ...safeProfile } = profile;
            safeProfile.pronouns = (safeProfile.pronouns || '').trim();
            await axios.put(`${API}/users/profile`, safeProfile, { headers: getAuthHeader() });
            toast.success('Profile updated');
        } catch { toast.error('Failed to update profile'); }
        finally { setSaving(false); }
    };

    return (
        <SettingsLayout title="Profile" description="Manage your personal information and preferences">
            <div className="max-w-2xl space-y-6">
                {/* Section 1: Basic Info */}
                <div className="border border-stone-200 rounded-lg p-5">
                    <h3 className="text-sm font-semibold text-stone-900 mb-4 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        <User className="w-4 h-4 text-stone-500" /> Basic Information
                    </h3>
                    <div className="flex items-center gap-4 mb-5">
                        <div className="relative group flex-shrink-0">
                            <div className="w-16 h-16 rounded-full overflow-hidden bg-stone-100">
                                {profile.picture ? <img src={profile.picture} alt={profile.name} className="w-full h-full object-cover" /> :
                                    <div className="w-full h-full flex items-center justify-center text-stone-700 font-bold text-xl">{profile.name?.charAt(0)}</div>}
                            </div>
                            <button
                                type="button"
                                onClick={() => profilePhotoRef.current?.click()}
                                disabled={uploadingPhoto}
                                className="absolute inset-0 rounded-full bg-black/0 group-hover:bg-black/40 flex items-center justify-center transition-colors cursor-pointer"
                            >
                                {uploadingPhoto
                                    ? <Loader2 className="w-5 h-5 text-white animate-spin" />
                                    : <Camera className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />}
                            </button>
                            <input ref={profilePhotoRef} type="file" accept="image/*" className="hidden"
                                onChange={e => { handleImageUpload(e.target.files[0], 'picture', 'picture_s3_key', setUploadingPhoto); e.target.value = ''; }} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="font-medium text-stone-900">{user?.name}</p>
                            <p className="text-sm text-stone-500">{user?.email}</p>
                            <p className="text-xs text-stone-400 mt-0.5 capitalize">{user?.role?.replace('_', ' ')}</p>
                            <div className="flex items-center gap-2 mt-1">
                                <button type="button" onClick={() => profilePhotoRef.current?.click()} disabled={uploadingPhoto}
                                    className="text-xs text-stone-500 hover:text-stone-700 font-medium">
                                    {uploadingPhoto ? 'Uploading...' : 'Change Photo'}
                                </button>
                                {(profile.picture || profile.picture_s3_key) && (
                                    <button type="button" onClick={() => removeImage('picture', 'picture_s3_key')}
                                        className="text-xs text-red-400 hover:text-red-600 font-medium flex items-center gap-0.5">
                                        <Trash2 className="w-3 h-3" /> Remove
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Display Name</label>
                            <input value={profile.name || ''} onChange={e => setProfile(p => ({ ...p, name: e.target.value }))}
                                data-testid="profile-name-input"
                                className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Position Title</label>
                            <input value={profile.title || ''} onChange={e => setProfile(p => ({ ...p, title: e.target.value }))}
                                placeholder="e.g. Senior Account Executive"
                                data-testid="profile-title-input"
                                className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Email</label>
                            <input value={user?.email || ''} disabled
                                className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-stone-50 text-stone-500 cursor-not-allowed" />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Phone</label>
                            <input value={profile.phone || ''} onChange={e => setProfile(p => ({ ...p, phone: e.target.value }))}
                                placeholder="+1 (555) 000-0000"
                                data-testid="profile-phone-input"
                                className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
                        </div>
                        <div>
                            <label htmlFor="profile-pronouns" className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Pronouns</label>
                            <PronounsPicker
                                id="profile-pronouns"
                                value={profile.pronouns || ''}
                                onChange={v => setProfile(p => ({ ...p, pronouns: v }))}
                                ariaLabel="Pronouns"
                            />
                        </div>
                        <div>
                            <label htmlFor="timezone" className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Timezone</label>
                            <TimezoneSelect
                                id="timezone"
                                value={profile.timezone || ''}
                                placeholder={`Browser default (${browserTimeZone()})`}
                                onChange={tz => setProfile(p => ({ ...p, timezone: tz }))}
                            />
                            <p className="text-xs text-stone-500 mt-1">
                                Meetings, bookings, and times you see will use this zone.
                            </p>
                        </div>
                    </div>
                    {!profile.picture_s3_key && (
                        <div className="mt-4">
                            <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Profile Picture URL</label>
                            <input value={profile.picture || ''} onChange={e => setProfile(p => ({ ...p, picture: e.target.value }))}
                                placeholder="https://... or use the upload button above"
                                data-testid="profile-picture-input"
                                className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
                            <p className="text-xs text-stone-400 mt-1">Or click your avatar above to upload a photo directly.</p>
                        </div>
                    )}
                    <div className="mt-4">
                        <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Bio / About Me</label>
                        <textarea value={profile.bio || ''} onChange={e => setProfile(p => ({ ...p, bio: e.target.value }))}
                            rows={3} placeholder="Tell people a bit about yourself..."
                            data-testid="profile-bio-input"
                            className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20 resize-none" />
                    </div>
                </div>

                {/* Referral ID (#793 S6) — what partner apps send to credit you */}
                <ReferralIdCard getAuthHeader={getAuthHeader} />

                {/* Section 2: Social Links */}
                <div className="border border-stone-200 rounded-lg p-5">
                    <h3 className="text-sm font-semibold text-stone-900 mb-4 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        <Link className="w-4 h-4 text-stone-500" /> Social Links
                    </h3>
                    <div className="space-y-3">
                        {[
                            { key: 'linkedin_url', label: 'LinkedIn', icon: Linkedin, placeholder: 'https://linkedin.com/in/yourprofile' },
                            { key: 'twitter_url', label: 'Twitter / X', icon: Twitter, placeholder: 'https://x.com/yourhandle' },
                            { key: 'facebook_url', label: 'Facebook', icon: Facebook, placeholder: 'https://facebook.com/yourprofile' },
                            { key: 'instagram_url', label: 'Instagram', icon: Instagram, placeholder: 'https://instagram.com/yourhandle' },
                            { key: 'personal_website', label: 'Personal Website', icon: Globe, placeholder: 'https://yourwebsite.com' },
                        ].map(social => {
                            const SocialIcon = social.icon;
                            return (
                                <div key={social.key} className="flex items-center gap-2">
                                    <SocialIcon className="w-4 h-4 text-stone-400 flex-shrink-0" />
                                    <input value={profile[social.key] || ''} onChange={e => setProfile(p => ({ ...p, [social.key]: e.target.value }))}
                                        placeholder={social.placeholder}
                                        className="flex-1 px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Section 2b: Appearance (Shell R2) */}
                <div className="border border-stone-200 rounded-lg p-5" data-testid="appearance-card">
                    <h3 className="text-sm font-semibold text-stone-900 mb-4 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        <Palette className="w-4 h-4 text-stone-500" /> Appearance
                    </h3>
                    <div className="space-y-3">
                        <div>
                            <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Theme</label>
                            <select
                                value={themePref}
                                onChange={e => setTheme(e.target.value)}
                                data-testid="theme-select"
                                className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20 bg-white"
                            >
                                <option value="porcelain">Porcelain (light)</option>
                                <option value="graphite">Graphite (beta — page content adopts progressively)</option>
                                <option value="system">Match system</option>
                            </select>
                            <p className="text-xs text-stone-400 mt-2">
                                Porcelain is the default warm-neutral shell. Graphite fully themes the shell today; individual pages adopt dark surfaces over time.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Section 3: Independent Consultant */}
                <div className="border border-stone-200 rounded-lg p-5">
                    <h3 className="text-sm font-semibold text-stone-900 mb-4 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        <Briefcase className="w-4 h-4 text-stone-500" /> Independent Consultant
                    </h3>
                    <label className="flex items-center gap-3 cursor-pointer mb-4">
                        <input type="checkbox" checked={profile.is_independent_consultant || false}
                            onChange={e => setProfile(p => ({ ...p, is_independent_consultant: e.target.checked }))}
                            data-testid="consultant-checkbox"
                            className="w-4 h-4 rounded border-stone-300 text-stone-900 focus:ring-stone-900/20" />
                        <div>
                            <span className="text-sm font-medium text-stone-700">I am an independent consultant / freelancer</span>
                            <p className="text-xs text-stone-400">Enable this to add your company details for use in signatures and emails</p>
                        </div>
                    </label>
                    {profile.is_independent_consultant && (
                        <div className="space-y-4 pt-2 border-t border-stone-100">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                                <div>
                                    <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Company Name</label>
                                    <input value={profile.company_name || ''} onChange={e => setProfile(p => ({ ...p, company_name: e.target.value }))}
                                        placeholder="Your company name"
                                        data-testid="company-name-input"
                                        className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Company Website</label>
                                    <input value={profile.company_website || ''} onChange={e => setProfile(p => ({ ...p, company_website: e.target.value }))}
                                        placeholder="https://yourcompany.com"
                                        className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Company Logo</label>
                                    <div className="flex items-center gap-3">
                                        {profile.company_logo_url ? (
                                            <div className="w-10 h-10 rounded border border-stone-200 overflow-hidden bg-white flex-shrink-0">
                                                <img src={profile.company_logo_url} alt="Logo" className="w-full h-full object-contain" />
                                            </div>
                                        ) : (
                                            <div className="w-10 h-10 rounded border border-dashed border-stone-300 bg-stone-50 flex items-center justify-center flex-shrink-0">
                                                <Building2 className="w-4 h-4 text-stone-300" />
                                            </div>
                                        )}
                                        <div className="flex items-center gap-2">
                                            <button type="button" onClick={() => companyLogoRef.current?.click()} disabled={uploadingLogo}
                                                className="px-3 py-1.5 text-xs font-medium border border-stone-200 rounded-lg hover:bg-stone-50 text-stone-600 flex items-center gap-1.5">
                                                {uploadingLogo ? <><Loader2 className="w-3 h-3 animate-spin" /> Uploading...</> : <><Upload className="w-3 h-3" /> Upload Logo</>}
                                            </button>
                                            {(profile.company_logo_url || profile.company_logo_s3_key) && (
                                                <button type="button" onClick={() => removeImage('company_logo_url', 'company_logo_s3_key')}
                                                    className="text-xs text-red-400 hover:text-red-600 font-medium flex items-center gap-0.5">
                                                    <Trash2 className="w-3 h-3" /> Remove
                                                </button>
                                            )}
                                        </div>
                                        <input ref={companyLogoRef} type="file" accept="image/*" className="hidden"
                                            onChange={e => { handleImageUpload(e.target.files[0], 'company_logo_url', 'company_logo_s3_key', setUploadingLogo); e.target.value = ''; }} />
                                    </div>
                                    {!profile.company_logo_s3_key && (
                                        <input value={profile.company_logo_url || ''} onChange={e => setProfile(p => ({ ...p, company_logo_url: e.target.value }))}
                                            placeholder="Or paste a URL: https://..."
                                            className="w-full mt-2 px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
                                    )}
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">ABN / Tax ID</label>
                                    <input value={profile.company_abn || ''} onChange={e => setProfile(p => ({ ...p, company_abn: e.target.value }))}
                                        placeholder="Optional"
                                        className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-2 flex items-center gap-1">
                                    <MapPin className="w-3 h-3" /> Company Address
                                </label>
                                <div className="space-y-3">
                                    <input value={profile.company_street || ''} onChange={e => setProfile(p => ({ ...p, company_street: e.target.value }))}
                                        placeholder="Street address"
                                        className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                        <input value={profile.company_city || ''} onChange={e => setProfile(p => ({ ...p, company_city: e.target.value }))}
                                            placeholder="City" className="px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
                                        <input value={profile.company_state || ''} onChange={e => setProfile(p => ({ ...p, company_state: e.target.value }))}
                                            placeholder="State" className="px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
                                        <input value={profile.company_zip || ''} onChange={e => setProfile(p => ({ ...p, company_zip: e.target.value }))}
                                            placeholder="ZIP" className="px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
                                        <input value={profile.company_country || ''} onChange={e => setProfile(p => ({ ...p, company_country: e.target.value }))}
                                            placeholder="Country" className="px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Merge Variables Info */}
                <div className="bg-stone-50 border border-stone-200 rounded-lg p-4">
                    <div className="flex items-start gap-2">
                        <FileText className="w-4 h-4 text-stone-400 mt-0.5 flex-shrink-0" />
                        <div>
                            <p className="text-xs font-medium text-stone-600">Your profile fields are available as merge variables</p>
                            <p className="text-xs text-stone-400 mt-0.5">
                                Use variables like <code className="bg-stone-200 px-1 rounded">{'{{SenderName}}'}</code>, <code className="bg-stone-200 px-1 rounded">{'{{SenderTitle}}'}</code>, <code className="bg-stone-200 px-1 rounded">{'{{SenderPhone}}'}</code>, <code className="bg-stone-200 px-1 rounded">{'{{SenderCompany}}'}</code> in email templates and signatures to auto-fill your details.
                            </p>
                        </div>
                    </div>
                </div>

                <button onClick={saveProfile} disabled={saving} data-testid="save-profile-btn"
                    className="px-5 py-2.5 bg-stone-900 text-white text-sm font-medium rounded-lg hover:bg-stone-800 disabled:opacity-50">
                    {saving ? 'Saving...' : 'Save Changes'}
                </button>
            </div>
        </SettingsLayout>
    );
}
